import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';
import { indianArtStars as legacyIndianArtStars } from '@/lib/indianArtStars';

const emptyForm = {
  linked_artist_id: '',
  slug: '',
  status: 'draft',
  heading: 'Stars of Indian Art',
  eyebrow: 'Managed by KalaManch Admin',
  star_title: '',
  known_as: '',
  specialty: '',
  short_intro: '',
  curatorial_note: '',
  region_label: '',
  facebook_url: '',
  external_website: '',
  secondary_website: '',
  portrait: '',
  cover_artwork: '',
  hero_badges: '',
  bio_paragraphs: '',
  profile_highlights: '',
  education: '',
  career: '',
  exhibitions: '',
  recognition: '',
  artworks: '',
};

const listToTextarea = (items = []) => (items || []).filter(Boolean).join('\n');
const textareaToList = (value = '') =>
  String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const artworksToTextarea = (items = []) =>
  (items || [])
    .map(
      (item) =>
        `${item.title || 'Artwork'} | ${item.src || item.url || ''} | ${
          item.is_published === false ? 'hidden' : 'live'
        }`.trim(),
    )
    .filter(Boolean)
    .join('\n');

const textareaToArtworks = (value = '') =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [title, url, visibility] = line.split('|').map((part) => part.trim());
      return url
        ? {
            id: `artwork-${index + 1}`,
            title: title || 'Artwork',
            src: url,
            is_published: visibility !== 'hidden',
            sort_order: index,
          }
        : null;
    })
    .filter(Boolean);

const normalizeArtworkItems = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => item?.src || item?.url)
    .map((item, index) => ({
      id: item.id || `artwork-${index + 1}`,
      title: item.title || 'Artwork',
      src: item.src || item.url || '',
      is_published: item.is_published !== false,
      sort_order:
        Number.isFinite(Number(item.sort_order)) ? Number(item.sort_order) : index,
    }))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const buildFormFromStar = (star) => ({
  linked_artist_id: star.linked_artist_id || '',
  slug: star.slug || '',
  status: star.status || 'draft',
  heading: star.heading || 'Stars of Indian Art',
  eyebrow: star.eyebrow || 'Managed by KalaManch Admin',
  star_title: star.star_title || '',
  known_as: star.known_as || '',
  specialty: star.specialty || '',
  short_intro: star.short_intro || '',
  curatorial_note: star.curatorial_note || '',
  region_label: star.region_label || '',
  facebook_url: star.facebook_url || '',
  external_website: star.external_website || '',
  secondary_website: star.secondary_website || '',
  portrait: star.portrait || '',
  cover_artwork: star.cover_artwork || '',
  hero_badges: listToTextarea(star.hero_badges),
  bio_paragraphs: listToTextarea(star.bio_paragraphs),
  profile_highlights: listToTextarea(star.profile_highlights),
  education: listToTextarea(star.education),
  career: listToTextarea(star.career),
  exhibitions: listToTextarea(star.exhibitions),
  recognition: listToTextarea(star.recognition),
  artworks: artworksToTextarea(star.artworks),
});

const buildPayload = (form) => ({
  linked_artist_id: form.linked_artist_id,
  slug: form.slug || undefined,
  status: form.status || 'draft',
  heading: form.heading || undefined,
  eyebrow: form.eyebrow || undefined,
  star_title: form.star_title || undefined,
  known_as: form.known_as || undefined,
  specialty: form.specialty || undefined,
  short_intro: form.short_intro || undefined,
  curatorial_note: form.curatorial_note || undefined,
  region_label: form.region_label || undefined,
  facebook_url: form.facebook_url || undefined,
  external_website: form.external_website || undefined,
  secondary_website: form.secondary_website || undefined,
  portrait: form.portrait || undefined,
  cover_artwork: form.cover_artwork || undefined,
  hero_badges: textareaToList(form.hero_badges),
  bio_paragraphs: textareaToList(form.bio_paragraphs),
  profile_highlights: textareaToList(form.profile_highlights),
  education: textareaToList(form.education),
  career: textareaToList(form.career),
  exhibitions: textareaToList(form.exhibitions),
  recognition: textareaToList(form.recognition),
  artworks: textareaToArtworks(form.artworks),
});

const InfoBlock = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
    <p className="text-xs font-black uppercase tracking-[0.22em] text-gray-500">{label}</p>
    <p className="mt-2 text-sm font-semibold text-gray-800">{value || '-'}</p>
  </div>
);

const FieldNote = ({ children }) => (
  <p className="mt-2 text-xs font-semibold leading-5 text-[#6B46C1]">{children}</p>
);

const FieldGroup = ({ children, note, className = '' }) => (
  <div className={className}>
    {children}
    {note ? <FieldNote>{note}</FieldNote> : null}
  </div>
);

const artworkTitleFromUrl = (url = '') => {
  const base = String(url || '').split('?')[0].split('#')[0];
  const file = base.split('/').filter(Boolean).pop() || 'Artwork';
  return file.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim() || 'Artwork';
};

export default function AdminIndianArtStars() {
  const [artistSearch, setArtistSearch] = useState('');
  const [starSearch, setStarSearch] = useState('');
  const [artists, setArtists] = useState([]);
  const [stars, setStars] = useState([]);
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [selectedStarId, setSelectedStarId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [busy, setBusy] = useState('');
  const [artworkDraft, setArtworkDraft] = useState({ title: '', src: '', selectedPortfolio: '' });
  const [artworkItems, setArtworkItems] = useState([]);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const [dragArtworkIndex, setDragArtworkIndex] = useState(null);
  const [loadingArtists, setLoadingArtists] = useState(true);
  const [loadingStars, setLoadingStars] = useState(true);

  const selectedArtist = useMemo(
    () => artists.find((artist) => artist.id === selectedArtistId) || null,
    [artists, selectedArtistId],
  );

  const selectedStar = useMemo(
    () => stars.find((star) => star.id === selectedStarId) || null,
    [stars, selectedStarId],
  );

  const linkedArtistPortfolioImages = useMemo(
    () => (selectedArtist?.portfolio_image_urls || []).filter(Boolean),
    [selectedArtist],
  );

  const legacyOnlyStars = useMemo(() => {
    const storedSlugs = new Set(stars.map((star) => star.slug).filter(Boolean));
    return legacyIndianArtStars.filter((star) => !storedSlugs.has(star.slug));
  }, [stars]);

  const loadArtists = async () => {
    setLoadingArtists(true);
    try {
      const response = await adminApi.get('/admin/indian-art-stars/artists', { params: { search: artistSearch } });
      const nextArtists = response.data?.artists || [];
      setArtists(nextArtists);
      setPageError('');
      if (!selectedArtistId && nextArtists[0]?.id) {
        setSelectedArtistId(nextArtists[0].id);
      }
    } catch (error) {
      setPageError(error.response?.data?.detail || 'Could not load artists.');
    } finally {
      setLoadingArtists(false);
    }
  };

  const loadStars = async (focusStarId = '') => {
    setLoadingStars(true);
    try {
      const response = await adminApi.get('/admin/indian-art-stars', { params: { search: starSearch } });
      const nextStars = response.data?.stars || [];
      setStars(nextStars);
      setPageError('');
      if (focusStarId) {
        setSelectedStarId(focusStarId);
      } else if (!selectedStarId && nextStars[0]?.id) {
        setSelectedStarId(nextStars[0].id);
      }
    } catch (error) {
      setPageError(error.response?.data?.detail || 'Could not load Indian Art stars.');
    } finally {
      setLoadingStars(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(loadArtists, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadStars(), 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starSearch]);

  useEffect(() => {
    if (!selectedStar) return;
    setForm(buildFormFromStar(selectedStar));
    setArtworkItems(normalizeArtworkItems(selectedStar.artworks));
    setSelectedArtistId(selectedStar.linked_artist_id || '');
  }, [selectedStar]);

  useEffect(() => {
    if (!selectedArtist || selectedStarId) return;
    setForm((current) => ({
      ...current,
      linked_artist_id: selectedArtist.id,
      star_title: current.star_title || selectedArtist.name || '',
      specialty: current.specialty || selectedArtist.subcategory || selectedArtist.category || '',
      region_label: current.region_label || selectedArtist.city || '',
      portrait: current.portrait || selectedArtist.image_url || '',
      cover_artwork: current.cover_artwork || selectedArtist.image_url || '',
    }));
  }, [selectedArtist, selectedStarId]);

  useEffect(() => {
    setForm((current) => ({ ...current, artworks: artworksToTextarea(artworkItems) }));
  }, [artworkItems]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleArtworkDraftChange = (field, value) => {
    setArtworkDraft((current) => ({ ...current, [field]: value }));
  };

  const syncArtworkRows = (rows) => {
    setArtworkItems(
      normalizeArtworkItems(
        rows.map((item, index) => ({
          ...item,
          sort_order: index,
        })),
      ),
    );
  };

  const addArtworkToExhibition = () => {
    const chosenUrl = artworkDraft.selectedPortfolio || artworkDraft.src;
    const cleanUrl = String(chosenUrl || '').trim();
    if (!cleanUrl) {
      setMessage('Please select an existing artist artwork or paste an artwork image URL first.');
      return;
    }
    const cleanTitle = String(artworkDraft.title || '').trim() || artworkTitleFromUrl(cleanUrl);
    const nextRows = [
      ...artworkItems,
      {
        id: `artwork-${Date.now()}`,
        title: cleanTitle,
        src: cleanUrl,
        is_published: true,
        sort_order: artworkItems.length,
      },
    ];
    syncArtworkRows(nextRows);
    setArtworkDraft({ title: '', src: '', selectedPortfolio: '' });
    setMessage(`Added "${cleanTitle}" to this exhibition draft.`);
  };

  const removeArtworkFromExhibition = (indexToRemove) => {
    syncArtworkRows(artworkItems.filter((_, index) => index !== indexToRemove));
  };

  const toggleArtworkPublished = (indexToToggle) => {
    syncArtworkRows(
      artworkItems.map((item, index) =>
        index === indexToToggle ? { ...item, is_published: item.is_published === false } : item,
      ),
    );
  };

  const moveArtwork = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= artworkItems.length || fromIndex === toIndex) return;
    const nextItems = [...artworkItems];
    const [moved] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, moved);
    syncArtworkRows(nextItems);
  };

  const handleArtworkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    setArtworkUploading(true);
    try {
      const response = await adminApi.post('/uploads/profile-image', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = response.data?.image_url || '';
      if (imageUrl) {
        setArtworkDraft((current) => ({
          ...current,
          title: current.title || artworkTitleFromUrl(imageUrl),
          src: imageUrl,
          selectedPortfolio: '',
        }));
        setMessage('Artwork uploaded. Review the title, then add it to the exhibition.');
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to upload artwork image.');
    } finally {
      setArtworkUploading(false);
      event.target.value = '';
    }
  };

  const resetForNew = () => {
    setSelectedStarId('');
    setForm({
      ...emptyForm,
      linked_artist_id: selectedArtist?.id || '',
      star_title: selectedArtist?.name || '',
      specialty: selectedArtist?.subcategory || selectedArtist?.category || '',
      region_label: selectedArtist?.city || '',
      portrait: selectedArtist?.image_url || '',
      cover_artwork: selectedArtist?.image_url || '',
    });
    setArtworkItems([]);
    setMessage('Ready to create a new Stars of Indian Art page.');
  };

  const buildLegacyPayload = (legacyStar) => {
    const linkedArtist =
      artists.find((artist) => artist.slug === String(legacyStar.profilePath || '').split('/').filter(Boolean).pop()) ||
      artists.find((artist) => artist.name === legacyStar.name);

    return {
      linked_artist_id: linkedArtist?.id || '',
      slug: legacyStar.slug,
      status: 'draft',
      heading: legacyStar.heading || 'Stars of Indian Art',
      eyebrow: legacyStar.eyebrow || 'Managed by KalaManch Admin',
      star_title: legacyStar.name || '',
      known_as: legacyStar.knownAs || '',
      specialty: legacyStar.specialty || '',
      short_intro: legacyStar.shortIntro || '',
      curatorial_note: legacyStar.curatorialNote || '',
      region_label: legacyStar.regionLabel || '',
      facebook_url: legacyStar.facebookUrl || '',
      external_website: legacyStar.externalWebsite || '',
      secondary_website: legacyStar.secondaryWebsite || '',
      portrait: legacyStar.portrait || '',
      cover_artwork: legacyStar.coverArtwork || '',
      hero_badges: legacyStar.heroBadges || [],
      bio_paragraphs: legacyStar.bioParagraphs || [],
      profile_highlights: legacyStar.profileHighlights || [],
      education: legacyStar.education || [],
      career: legacyStar.career || [],
      exhibitions: legacyStar.exhibitions || [],
      recognition: legacyStar.recognition || [],
      artworks: normalizeArtworkItems(legacyStar.artworks || []),
    };
  };

  const importLegacyStars = async () => {
    if (!legacyOnlyStars.length) {
      setMessage('Legacy curated stars are already available in admin records.');
      return;
    }
    setBusy('import-legacy');
    try {
      let imported = 0;
      let skipped = 0;
      for (const legacyStar of legacyOnlyStars) {
        const payload = buildLegacyPayload(legacyStar);
        if (!payload.linked_artist_id) {
          skipped += 1;
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        await adminApi.post('/admin/indian-art-stars', payload);
        imported += 1;
      }
      setMessage(
        imported
          ? `Imported ${imported} legacy curated star page${imported === 1 ? '' : 's'} into admin records.${skipped ? ` Skipped ${skipped} because the linked artist could not be matched.` : ''}`
          : 'No legacy stars were imported because the linked artists could not be matched yet.',
      );
      await Promise.all([loadStars(), loadArtists()]);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not import legacy curated star pages.');
    } finally {
      setBusy('');
    }
  };

  const saveStar = async () => {
    if (!form.linked_artist_id) {
      setMessage('Please select a linked artist first.');
      return;
    }
    setBusy('save');
    try {
      const payload = { ...buildPayload(form), artworks: artworkItems };
      const response = selectedStarId
        ? await adminApi.put(`/admin/indian-art-stars/${selectedStarId}`, payload)
        : await adminApi.post('/admin/indian-art-stars', payload);
      const savedStar = response.data?.star;
      setMessage(selectedStarId ? 'Indian Art star updated.' : 'Indian Art star created.');
      if (savedStar) {
        setSelectedStarId(savedStar.id);
        setForm(buildFormFromStar(savedStar));
        setArtworkItems(normalizeArtworkItems(savedStar.artworks));
        await loadStars(savedStar.id);
      } else {
        await loadStars();
      }
      await loadArtists();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not save Indian Art star.');
    } finally {
      setBusy('');
    }
  };

  const publishStar = async () => {
    if (!selectedStarId) return;
    setBusy('publish');
    try {
      await adminApi.post(`/admin/indian-art-stars/${selectedStarId}/publish`);
      setMessage('Indian Art star published and synced to the linked artist.');
      await Promise.all([loadStars(selectedStarId), loadArtists()]);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not publish Indian Art star.');
    } finally {
      setBusy('');
    }
  };

  const unpublishStar = async () => {
    if (!selectedStarId) return;
    setBusy('unpublish');
    try {
      await adminApi.post(`/admin/indian-art-stars/${selectedStarId}/unpublish`);
      setMessage('Indian Art star unpublished. Artist card marker removed.');
      await Promise.all([loadStars(selectedStarId), loadArtists()]);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not unpublish Indian Art star.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm lg:p-8">
        <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Art Exhibition</p>
        <h1 className="mt-3 text-3xl font-black text-gray-950">Stars of Indian Art</h1>
        <p className="mt-3 max-w-3xl text-gray-600">
          Create and update Stars of Indian Art pages. For the Art Exhibition wall: choose an artist,
          upload or select artwork, add it, delete or reorder as needed, then save and publish.
        </p>
      </section>

      {pageError && (
        <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-3 font-bold text-orange-800">
          {pageError}
        </div>
      )}

      {message && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-3 font-bold text-emerald-800">
          {message}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[340px_340px_1fr]">
        <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Artists</p>
              <h2 className="mt-1 text-2xl font-black text-gray-950">Choose linked artist</h2>
            </div>
            <button type="button" onClick={resetForNew} className="btn-outline">
              New draft
            </button>
          </div>
          <input
            value={artistSearch}
            onChange={(event) => setArtistSearch(event.target.value)}
            placeholder="Search artist, city, category"
            className="mt-4 w-full rounded-2xl border border-gray-200 px-4 py-3"
          />
          <FieldNote>
            Search the KalaManch artist you want to connect to this curated page. Pick the exact artist first so the star marker syncs back to the correct public profile.
          </FieldNote>
          <div className="mt-4 max-h-[36rem] space-y-2 overflow-y-auto pr-1">
            {loadingArtists ? (
              <p className="rounded-2xl bg-gray-50 p-4 font-bold text-gray-500">Loading artists...</p>
            ) : artists.length === 0 ? (
              <p className="rounded-2xl bg-gray-50 p-4 font-bold text-gray-500">No artists found.</p>
            ) : (
              artists.map((artist) => (
                <button
                  key={artist.id}
                  type="button"
                  onClick={() => {
                    setSelectedArtistId(artist.id);
                    if (!selectedStarId) {
                      setForm((current) => ({
                        ...current,
                        linked_artist_id: artist.id,
                        star_title: current.star_title || artist.name || '',
                        specialty: current.specialty || artist.subcategory || artist.category || '',
                        region_label: current.region_label || artist.city || '',
                        portrait: current.portrait || artist.image_url || '',
                        cover_artwork: current.cover_artwork || artist.image_url || '',
                      }));
                    }
                  }}
                  className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                    selectedArtistId === artist.id ? 'border-[#FF6F00] bg-orange-50' : 'border-gray-100 bg-white hover:bg-gray-50'
                  }`}
                >
                  {artist.image_url ? (
                    <img src={artist.image_url} alt="" className="h-12 w-12 rounded-xl object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 font-black">K</span>
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-black text-gray-950">{artist.name}</span>
                    <span className="block truncate text-xs font-semibold text-gray-500">
                      {artist.subcategory || artist.category || 'Artist'} {artist.city ? `- ${artist.city}` : ''}
                    </span>
                    {artist.indian_art_star_slug && (
                      <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-black text-amber-700">
                        Star linked
                      </span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Existing pages</p>
          <h2 className="mt-1 text-2xl font-black text-gray-950">Curated star records</h2>
          <input
            value={starSearch}
            onChange={(event) => setStarSearch(event.target.value)}
            placeholder="Search star page, slug, specialty"
            className="mt-4 w-full rounded-2xl border border-gray-200 px-4 py-3"
          />
          <FieldNote>
            Search admin-managed star pages by artist name, page slug, or specialty. Older curated star pages from the earlier file-managed setup can be imported here.
          </FieldNote>
          <div className="mt-4 max-h-[36rem] space-y-2 overflow-y-auto pr-1">
            {loadingStars ? (
              <p className="rounded-2xl bg-gray-50 p-4 font-bold text-gray-500">Loading star pages...</p>
            ) : stars.length === 0 ? (
              <div className="space-y-3 rounded-2xl bg-gray-50 p-4">
                <p className="font-bold text-gray-700">No admin-managed star pages yet.</p>
                {legacyOnlyStars.length ? (
                  <>
                    <p className="text-sm font-semibold text-gray-500">
                      KalaManch still has {legacyOnlyStars.length} curated star page{legacyOnlyStars.length === 1 ? '' : 's'} from the earlier setup. Import them once here so future edits happen from admin.
                    </p>
                    <button
                      type="button"
                      onClick={importLegacyStars}
                      disabled={Boolean(busy)}
                      className="btn-outline"
                    >
                      {busy === 'import-legacy' ? 'Importing legacy stars...' : 'Import existing curated stars'}
                    </button>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-gray-500">Create a new star page or pick a linked artist to start a curated draft.</p>
                )}
              </div>
            ) : (
              stars.map((star) => (
                <button
                  key={star.id}
                  type="button"
                  onClick={() => setSelectedStarId(star.id)}
                  className={`w-full rounded-2xl border p-3 text-left transition ${
                    selectedStarId === star.id ? 'border-[#4A148C] bg-purple-50' : 'border-gray-100 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-black text-gray-950">{star.name}</p>
                      <p className="truncate text-xs font-semibold text-gray-500">{star.slug}</p>
                      <p className="mt-1 truncate text-xs font-semibold text-[#4A148C]">{star.specialty || star.category || 'Indian Art star'}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] font-black ${
                      star.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {star.status || 'draft'}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </section>

        <section className="space-y-6">
          <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Star editor</p>
                <h2 className="mt-1 text-2xl font-black text-gray-950">{selectedStar?.name || selectedArtist?.name || 'New curated page'}</h2>
                <p className="mt-2 text-sm text-gray-600">Use one artist, then publish when the page is ready for public viewing.</p>
              </div>
              {(selectedStar?.slug || form.slug) && (
                <Link
                  to={`/indian-art/stars/${selectedStar?.slug || form.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline text-center"
                >
                  Open public page
                </Link>
              )}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <InfoBlock label="Linked artist" value={selectedArtist?.name || selectedStar?.name || 'Select from artist list'} />
              <InfoBlock label="Current status" value={selectedStar?.status || form.status || 'draft'} />
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <FieldGroup note="Use short lowercase words separated by hyphens. Example: santosh-kumar-sandilya. This becomes the public page URL.">
                <input value={form.slug} onChange={(event) => handleChange('slug', event.target.value)} placeholder="Slug" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Keep draft while building. Switch to published only when the page is ready for public viewing.">
                <select value={form.status} onChange={(event) => handleChange('status', event.target.value)} className="rounded-2xl border border-gray-200 px-4 py-3">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </FieldGroup>
              <FieldGroup note="Main heading shown at the top of the public star page. Usually keep this as Stars of Indian Art.">
                <input value={form.heading} onChange={(event) => handleChange('heading', event.target.value)} placeholder="Heading" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Small top label above the heading. Use this for curator or admin context.">
                <input value={form.eyebrow} onChange={(event) => handleChange('eyebrow', event.target.value)} placeholder="Eyebrow" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Public artist title exactly as it should be displayed on the curated page.">
                <input value={form.star_title} onChange={(event) => handleChange('star_title', event.target.value)} placeholder="Star title" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Use a public alias, studio name, or a popularly known identity if it differs from the linked artist name.">
                <input value={form.known_as} onChange={(event) => handleChange('known_as', event.target.value)} placeholder="Known as" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Enter the signature Indian art form, such as Rangoli Artist, Banaras Painter, or Pottery Studio.">
                <input value={form.specialty} onChange={(event) => handleChange('specialty', event.target.value)} placeholder="Specialty" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Add the city, region, or cultural location to show on the public page.">
                <input value={form.region_label} onChange={(event) => handleChange('region_label', event.target.value)} placeholder="Region label" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup className="md:col-span-2" note="Paste the main portrait or recognizable artist image URL. This becomes the key face image for the star page.">
                <input value={form.portrait} onChange={(event) => handleChange('portrait', event.target.value)} placeholder="Portrait image URL" className="rounded-2xl border border-gray-200 px-4 py-3 md:col-span-2" />
              </FieldGroup>
              <FieldGroup className="md:col-span-2" note="Paste the strongest hero artwork image URL that best represents the artist’s work.">
                <input value={form.cover_artwork} onChange={(event) => handleChange('cover_artwork', event.target.value)} placeholder="Cover artwork URL" className="rounded-2xl border border-gray-200 px-4 py-3 md:col-span-2" />
              </FieldGroup>
              <FieldGroup note="Main website, portfolio, or official external destination for the artist.">
                <input value={form.external_website} onChange={(event) => handleChange('external_website', event.target.value)} placeholder="External website" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Optional second external destination like an archive, store, or supporting portfolio page.">
                <input value={form.secondary_website} onChange={(event) => handleChange('secondary_website', event.target.value)} placeholder="Secondary website" className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup className="md:col-span-2" note="Official Facebook profile or page URL for the featured artist, if available.">
                <input value={form.facebook_url} onChange={(event) => handleChange('facebook_url', event.target.value)} placeholder="Facebook URL" className="rounded-2xl border border-gray-200 px-4 py-3 md:col-span-2" />
              </FieldGroup>
            </div>

            <div className="mt-4 grid gap-4">
              <FieldGroup note="Write a 2 to 4 line summary that explains why this artist matters and what kind of work visitors are about to see.">
                <textarea value={form.short_intro} onChange={(event) => handleChange('short_intro', event.target.value)} placeholder="Short intro" rows={3} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="This is KalaManch’s curator note. Use it to explain why the artist belongs in Stars of Indian Art.">
                <textarea value={form.curatorial_note} onChange={(event) => handleChange('curatorial_note', event.target.value)} placeholder="Curatorial note" rows={4} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <FieldGroup note="One badge per line. Good examples: Admin Curated, Traditional Pottery, Featured Artist.">
                <textarea value={form.hero_badges} onChange={(event) => handleChange('hero_badges', event.target.value)} placeholder="Hero badges (one per line)" rows={5} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="One short highlight per line, such as awards, regional identity, signature style, or lineage.">
                <textarea value={form.profile_highlights} onChange={(event) => handleChange('profile_highlights', event.target.value)} placeholder="Profile highlights (one per line)" rows={5} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="Main biography content. Add each paragraph on a new line so the public page stays well spaced and readable.">
                <textarea value={form.bio_paragraphs} onChange={(event) => handleChange('bio_paragraphs', event.target.value)} placeholder="Bio paragraphs (one per line)" rows={8} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="One education or training item per line. Use formal study, lineage training, workshops, or related learning milestones.">
                <textarea value={form.education} onChange={(event) => handleChange('education', event.target.value)} placeholder="Education (one per line)" rows={6} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="One career milestone, professional role, or important journey note per line.">
                <textarea value={form.career} onChange={(event) => handleChange('career', event.target.value)} placeholder="Career (one per line)" rows={6} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="One exhibition, art fair, public showcase, workshop circuit, or event appearance per line.">
                <textarea value={form.exhibitions} onChange={(event) => handleChange('exhibitions', event.target.value)} placeholder="Exhibitions (one per line)" rows={6} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
              <FieldGroup note="One award, recognition, collection note, or public honor per line.">
                <textarea value={form.recognition} onChange={(event) => handleChange('recognition', event.target.value)} placeholder="Recognition (one per line)" rows={6} className="rounded-2xl border border-gray-200 px-4 py-3" />
              </FieldGroup>
            </div>

            <div className="mt-6 rounded-[1.8rem] border border-emerald-100 bg-emerald-50/50 p-5">
              <p className="text-xs font-black uppercase tracking-[0.25em] text-[#00A68C]">Add artwork to exhibition</p>
              <h3 className="mt-2 text-2xl font-black text-gray-950">Curate the public gallery wall</h3>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                Simple flow: choose the linked artist above, upload or select one artwork image, add a short artwork title if needed, then save. The public wall automatically links the artwork to that artist&apos;s profile.
              </p>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <FieldGroup note="Optional. Pick an image that already exists on the selected artist profile.">
                  <select
                    value={artworkDraft.selectedPortfolio}
                    onChange={(event) => handleArtworkDraftChange('selectedPortfolio', event.target.value)}
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
                  >
                    <option value="">Select artist portfolio image</option>
                    {linkedArtistPortfolioImages.map((imageUrl) => (
                      <option key={imageUrl} value={imageUrl}>
                        {artworkTitleFromUrl(imageUrl)}
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup note="Optional. Keep it short, for example: Ganga Ghat Painting, Maa Durga Rangoli, Terracotta Birds.">
                  <input
                    value={artworkDraft.title}
                    onChange={(event) => handleArtworkDraftChange('title', event.target.value)}
                    placeholder="Artwork title"
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
                  />
                </FieldGroup>
                <FieldGroup className="lg:col-span-2" note="Optional fallback. Usually use the Upload artwork image button below instead.">
                  <input
                    value={artworkDraft.src}
                    onChange={(event) => handleArtworkDraftChange('src', event.target.value)}
                    placeholder="Artwork image URL, only if you are not uploading from this device"
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
                  />
                </FieldGroup>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <label className="btn-outline cursor-pointer">
                  {artworkUploading ? 'Uploading artwork...' : 'Upload artwork image'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleArtworkUpload} />
                </label>
                <button type="button" onClick={addArtworkToExhibition} className="btn-primary">
                  Add artwork
                </button>
                <button
                  type="button"
                  onClick={() => setArtworkDraft({ title: '', src: '', selectedPortfolio: '' })}
                  className="btn-outline"
                >
                  Clear artwork draft
                </button>
              </div>

              <div className="mt-5 space-y-3">
                {artworkItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-4 py-4 text-sm font-semibold text-gray-500">
                    No artworks added yet. Upload or select an artwork, click Add artwork, then save this page.
                  </div>
                ) : (
                  artworkItems.map((artwork, index) => (
                    <div
                      key={artwork.id || `${artwork.src}-${index}`}
                      draggable
                      onDragStart={() => setDragArtworkIndex(index)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (dragArtworkIndex === null) return;
                        moveArtwork(dragArtworkIndex, index);
                        setDragArtworkIndex(null);
                      }}
                      onDragEnd={() => setDragArtworkIndex(null)}
                      className={`flex flex-col gap-4 rounded-2xl border bg-white p-4 lg:flex-row lg:items-center ${
                        dragArtworkIndex === index ? 'border-[#FF6F00] shadow-md' : 'border-emerald-100'
                      }`}
                    >
                      <img src={artwork.src} alt={artwork.title} className="h-24 w-24 rounded-2xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-lg font-black text-gray-950">{artwork.title}</p>
                        <p className="mt-1 text-xs font-semibold text-gray-500">
                          Linked artist: {selectedArtist?.name || form.star_title || 'Choose an artist above'}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => toggleArtworkPublished(index)}
                            className={`rounded-full px-3 py-2 text-xs font-black ${
                              artwork.is_published === false
                                ? 'border border-gray-200 bg-gray-100 text-gray-600'
                                : 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                            }`}
                          >
                            {artwork.is_published === false ? 'Hidden from wall' : 'Published on wall'}
                          </button>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-black text-slate-500">
                            Drag to reorder
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => moveArtwork(index, index - 1)}
                          disabled={index === 0}
                          className="rounded-full border border-slate-200 px-4 py-2 text-sm font-black text-slate-600 disabled:opacity-40"
                        >
                          Move up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveArtwork(index, index + 1)}
                          disabled={index === artworkItems.length - 1}
                          className="rounded-full border border-slate-200 px-4 py-2 text-sm font-black text-slate-600 disabled:opacity-40"
                        >
                          Move down
                        </button>
                        <button
                          type="button"
                          onClick={() => removeArtworkFromExhibition(index)}
                          className="rounded-full border border-red-200 px-4 py-2 text-sm font-black text-red-600"
                        >
                          Delete artwork
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={saveStar} disabled={Boolean(busy)} className="btn-primary">
                {busy === 'save' ? 'Saving...' : selectedStarId ? 'Save changes' : 'Create star page'}
              </button>
              <button type="button" onClick={publishStar} disabled={!selectedStarId || Boolean(busy)} className="rounded-full bg-emerald-600 px-5 py-3 font-black text-white disabled:opacity-50">
                {busy === 'publish' ? 'Publishing...' : 'Publish'}
              </button>
              <button type="button" onClick={unpublishStar} disabled={!selectedStarId || Boolean(busy)} className="rounded-full border border-gray-200 px-5 py-3 font-black text-gray-700 disabled:opacity-50">
                {busy === 'unpublish' ? 'Unpublishing...' : 'Unpublish'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
